import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  AsciiPanel,
  AsciiButton,
  AsciiStatusBadge,
  AsciiLoader,
  AsciiModal,
} from "@/components/ui/ascii-theme";
import VideoPlayer from "./VideoPlayer";

interface Video {
  video_id: string;
  original_filename: string;
  file_size: number;
  upload_timestamp: string;
  status: string;
  file_extension: string;
}

const VideoList: React.FC = () => {
  const [videos, setVideos] = useState<Video[]>([]);
  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingVideoId, setDeletingVideoId] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [videoToDelete, setVideoToDelete] = useState<Video | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch("/api/videos", {
        credentials: 'include'
      });
      const data = await response.json();

      if (response.ok) {
        setVideos(data.videos || []);
        // Auto-select first video if available and no video is currently selected
        if (data.videos && data.videos.length > 0 && !selectedVideo) {
          setSelectedVideo(data.videos[0]);
        }
      } else {
        setError(data.error || "Failed to fetch videos");
      }
    } catch (err: any) {
      setError("Network error: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVideoSelect = (video: Video) => {
    setSelectedVideo(video);
  };

  const handleDeleteVideo = async (videoId: string) => {
    try {
      setDeletingVideoId(videoId);
      const response = await fetch(`/api/videos/${videoId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (response.ok) {
        // Remove from local state
        const updatedVideos = videos.filter((v) => v.video_id !== videoId);
        setVideos(updatedVideos);

        // Clear selection if deleted video was selected
        if (selectedVideo && selectedVideo.video_id === videoId) {
          setSelectedVideo(updatedVideos.length > 0 ? updatedVideos[0] : null);
        }
        setShowDeleteModal(false);
        setVideoToDelete(null);
      } else {
        const data = await response.json();
        setError("Failed to delete video: " + (data.error || "Unknown error"));
      }
    } catch (err: any) {
      setError("Network error: " + err.message);
    } finally {
      setDeletingVideoId(null);
    }
  };

  const confirmDelete = (video: Video) => {
    setVideoToDelete(video);
    setShowDeleteModal(true);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const formatDate = (isoString: string): string => {
    const date = new Date(isoString);
    const dateStr = date.toLocaleDateString([], {
      month: "2-digit",
      day: "2-digit",
      year: "2-digit",
    });
    const timeStr = date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${dateStr} ${timeStr}`;
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <AsciiLoader text="Loading video library" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="border-2 border-red-600 p-3 sm:p-4 bg-red-50">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <span className="text-xs sm:text-sm font-bold text-red-600">
            ✗ ERROR: {error}
          </span>
          <AsciiButton variant="default" size="sm" onClick={fetchVideos}>
            ⟳ RETRY
          </AsciiButton>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-b-2 border-black pb-3">
        <div className="flex items-center gap-2">
          <span className="text-base sm:text-lg font-bold uppercase">
            📹 Video Library
          </span>
          <span className="text-xs border-2 border-black px-2 py-1 bg-[#fbf1c7]">
            {videos.length} VIDEOS
          </span>
        </div>
        <AsciiButton variant="default" size="sm" onClick={fetchVideos}>
          ⟳ REFRESH
        </AsciiButton>
      </div>

      {videos.length === 0 ? (
        <AsciiPanel title="═══ NO VIDEOS ═══">
          <div className="flex flex-col items-center justify-center py-8 sm:py-12">
            <pre className="text-2xl sm:text-4xl mb-4">📹</pre>
            <p className="text-xs sm:text-sm font-bold uppercase mb-2">
              No videos uploaded yet
            </p>
            <p className="text-xs opacity-60">
              Upload a video to get started with your library!
            </p>
          </div>
        </AsciiPanel>
      ) : (
        <div className="grid gap-4 sm:gap-6 lg:grid-cols-2">
          {/* Video Player Section */}
          <div className="order-2 lg:order-1">
            <VideoPlayer
              videoId={selectedVideo?.video_id}
              originalFilename={selectedVideo?.original_filename}
              onError={(error) => console.error("Video player error:", error)}
            />
          </div>

          {/* Video List Section */}
          <div className="order-1 lg:order-2">
            <AsciiPanel title={`═══ VIDEOS (${videos.length}) ═══`}>
              <div className="space-y-2">
                {videos.map((video) => {
                  const isActive = selectedVideo?.video_id === video.video_id;
                  return (
                    <div
                      key={video.video_id}
                      className={`border-2 transition-colors cursor-pointer p-2 sm:p-3 ${
                        isActive
                          ? "bg-[#fbf1c7] border-black"
                          : "border-black hover:bg-[#fbf1c7]"
                      }`}
                      onClick={() => handleVideoSelect(video)}
                    >
                      {/* Video Info */}
                      <div className="space-y-2">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="text-xs sm:text-sm font-bold uppercase truncate">
                              {video.original_filename}
                            </div>
                            <div className="text-[0.65rem] sm:text-xs opacity-60 mt-1">
                              📅 {formatDate(video.upload_timestamp)}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <span
                              className={`text-[0.6rem] sm:text-xs border border-black px-1.5 py-0.5 ${
                                video.status.toLowerCase() === "error"
                                  ? "bg-red-100"
                                  : "bg-white"
                              }`}
                            >
                              {video.status.toUpperCase()}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center justify-between gap-2 text-[0.65rem] sm:text-xs opacity-60">
                          <span>💾 {formatFileSize(video.file_size)}</span>
                          <span>{video.file_extension.toUpperCase()}</span>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-wrap gap-1.5 pt-2 border-t border-black">
                          <a
                            href={`/api/videos/${video.video_id}/download`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <AsciiButton variant="default" size="sm">
                              ⬇
                            </AsciiButton>
                          </a>

                          <AsciiButton
                            variant="danger"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              confirmDelete(video);
                            }}
                            disabled={deletingVideoId === video.video_id}
                          >
                            {deletingVideoId === video.video_id ? "..." : "🗑"}
                          </AsciiButton>

                          <AsciiButton
                            variant="default"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/asset_viewer/${video.video_id}`);
                            }}
                          >
                            ⤢
                          </AsciiButton>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </AsciiPanel>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <AsciiModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="⚠ CONFIRM DELETE ⚠"
        footer={
          <div className="flex gap-2 justify-end">
            <AsciiButton
              onClick={() => {
                setShowDeleteModal(false);
                setVideoToDelete(null);
              }}
              variant="default"
              disabled={!!deletingVideoId}
            >
              CANCEL
            </AsciiButton>
            <AsciiButton
              onClick={() =>
                videoToDelete && handleDeleteVideo(videoToDelete.video_id)
              }
              variant="danger"
              disabled={!!deletingVideoId}
            >
              {deletingVideoId ? "DELETING..." : "DELETE"}
            </AsciiButton>
          </div>
        }
      >
        <div className="space-y-3">
          <p className="text-xs sm:text-sm">
            Are you sure you want to delete this video?
          </p>
          {videoToDelete && (
            <div className="border-2 border-black p-2 bg-[#fbf1c7] text-xs">
              <span className="font-bold">FILE:</span>{" "}
              {videoToDelete.original_filename}
            </div>
          )}
          <p className="text-xs font-bold text-red-600">
            ⚠ This action cannot be undone!
          </p>
        </div>
      </AsciiModal>
    </div>
  );
};

export default VideoList;
